import { useEffect, useState } from 'react'
import { getOrgOverview } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

// Honest coverage line for team home (DW-ORG-DIES-TEAM-HOME).
// Carries the truth OrgDashboard used to surface: when mailboxes exist and
// some have stopped reporting (stale/error), the Concierge says so in plain
// language. All reporting → nothing extra. Fetch failure / non-admin → nothing
// (never a fake all-clear). Gated on isOrgAdmin so non-owner clients never hit
// /org/overview. No new backend endpoint.

function countPhrase(n: number): string {
  if (n === 1) return 'One of your mailboxes has'
  if (n === 2) return 'Two of your mailboxes have'
  return `${n} of your mailboxes have`
}

export function HomeMailboxCoverageLine() {
  const isOrgAdmin = useAuthStore((s) => s.isOrgAdmin)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!isOrgAdmin) {
      setMessage(null)
      return
    }
    let cancelled = false
    setMessage(null)
    void (async () => {
      try {
        const overview = await getOrgOverview()
        if (cancelled) return
        const accounts = overview.seats?.accounts ?? 0
        if (accounts <= 0) {
          setMessage(null)
          return
        }
        const stopped =
          (overview.coverage?.error ?? 0) + (overview.coverage?.degraded ?? 0)
        if (stopped <= 0) {
          setMessage(null)
          return
        }
        setMessage(
          `${countPhrase(stopped)} stopped reporting. Shall we have your admin reconnect them?`,
        )
      } catch {
        // Fetch failure / 403 → render nothing. Never invent an all-clear.
        if (!cancelled) setMessage(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOrgAdmin])

  if (!message) return null

  return (
    <div
      role="status"
      aria-label="Mailbox coverage"
      className="rounded-2xl p-4 mb-5 border border-warning/20 bg-warning-light"
    >
      <p className="text-sm text-navy whitespace-pre-wrap break-words">{message}</p>
    </div>
  )
}
